/**
 * @file PatientHome.jsx — /mi-salud
 */
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { CalendarDays, Clock3, CheckCircle2 } from "lucide-react";
import { usePatientDashboard } from "../hooks/usePatients";
import { PATIENT_STRINGS, APPOINTMENT_STRINGS } from "../constants/strings";
import { Button, Card, Chip } from "../ui";
import "./PatientHome.css";

const STATUS_TONES = {
  AGENDADA: "warn",
  CONFIRMADA: "ok",
  COMPLETADA: "primary",
  CANCELADA: "neutral",
  NO_ASISTIO: "err",
};

function PageSkeleton() {
  return (
    <div className="ph-page">
      <div className="sk-line sk-w-50 sk-h-32" />
      <div className="sk-block sk-h-110" />
      <div className="ph-stats">
        <div className="sk-block sk-h-72" />
        <div className="sk-block sk-h-72" />
      </div>
    </div>
  );
}

export default function PatientHome() {
  const { stats, isLoading } = usePatientDashboard();
  if (isLoading) return <PageSkeleton />;

  const next = stats?.nextAppointment;
  const proName = next
    ? `${next.professional?.user?.firstName || ""} ${next.professional?.user?.lastName || ""}`.trim()
    : "";

  return (
    <div className="ph-page">
      <header className="ph-head">
        <span className="ph-eyebrow">Mi salud</span>
        <h1 className="ph-title">{PATIENT_STRINGS.HOME_TITLE}</h1>
      </header>

      {next ? (
        <Card accent className="ph-next">
          <RouterLink to={`/cita/${next.id}`} className="ph-next-link">
            <span className="ph-next-eyebrow">{PATIENT_STRINGS.NEXT_APPOINTMENT}</span>
            <h2 className="ph-next-name">{proName}</h2>
            {next.professional?.specialty && (
              <span className="ph-next-specialty">{next.professional.specialty}</span>
            )}
            <div className="ph-next-when">
              <CalendarDays size={14} />
              <span>
                {new Date(next.dateTime).toLocaleDateString("es-MX", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}{" "}
                a las{" "}
                {new Date(next.dateTime).toLocaleTimeString("es-MX", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            <div className="ph-next-foot">
              <Chip tone={STATUS_TONES[next.status] || "neutral"} size="sm">
                {APPOINTMENT_STRINGS.STATUS_LABELS[next.status] || next.status}
              </Chip>
              <span className="ph-next-cta">{PATIENT_STRINGS.VIEW_DETAIL} →</span>
            </div>
          </RouterLink>
        </Card>
      ) : (
        <Card className="ph-empty">
          <CalendarDays size={36} className="ph-empty-icon" />
          <p className="ph-empty-text">{PATIENT_STRINGS.NO_UPCOMING}</p>
          <Button asChild>
            <RouterLink to="/mis-profesionales">Buscar un profesional</RouterLink>
          </Button>
        </Card>
      )}

      <div className="ph-stats">
        <Card className="ph-stat">
          <span className="ph-stat-icon ph-stat-icon-warn">
            <Clock3 size={18} />
          </span>
          <span className="ph-stat-value">{stats?.upcomingCount ?? 0}</span>
          <span className="ph-stat-label">{PATIENT_STRINGS.UPCOMING_COUNT}</span>
        </Card>
        <Card className="ph-stat">
          <span className="ph-stat-icon ph-stat-icon-ok">
            <CheckCircle2 size={18} />
          </span>
          <span className="ph-stat-value">{stats?.completedCount ?? 0}</span>
          <span className="ph-stat-label">{PATIENT_STRINGS.COMPLETED_COUNT}</span>
        </Card>
      </div>
    </div>
  );
}
